function waitForAnimationFrames(frameCount = 2) {
  return new Promise(resolve => {
    const step = remaining => {
      if (remaining <= 0) {
        resolve();
        return;
      }

      if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
        setTimeout(() => step(remaining - 1), 16);
        return;
      }

      window.requestAnimationFrame(() => step(remaining - 1));
    };

    step(frameCount);
  });
}

function isElementVisibleInViewport(element) {
  if (!element || typeof element.getBoundingClientRect !== "function") return false;

  const rect = element.getBoundingClientRect();
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth || 0 : 0;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight || 0 : 0;

  return rect.width > 0
    && rect.height > 0
    && rect.bottom > 0
    && rect.right > 0
    && rect.top < viewportHeight
    && rect.left < viewportWidth;
}

function waitForIframeVisibility(iframe, { timeoutMs = 1200, threshold = 0.05 } = {}) {
  if (!iframe || typeof window === "undefined") {
    return Promise.resolve();
  }

  if (isElementVisibleInViewport(iframe)) {
    return Promise.resolve();
  }

  return new Promise(resolve => {
    let resolved = false;
    let observer = null;
    let timeoutId = null;

    const finish = () => {
      if (resolved) return;
      resolved = true;
      if (observer) observer.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
      resolve();
    };

    timeoutId = setTimeout(finish, timeoutMs);

    if (typeof IntersectionObserver === "function") {
      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= threshold)) {
          finish();
        }
      }, { threshold: [threshold] });

      observer.observe(iframe);
      return;
    }

    const poll = () => {
      if (resolved) return;
      if (isElementVisibleInViewport(iframe)) {
        finish();
        return;
      }
      setTimeout(poll, 50);
    };

    poll();
  });
}

function waitForIframeStableSize(iframe, { timeoutMs = 1800, stableMs = 120 } = {}) {
  if (!iframe || typeof iframe.getBoundingClientRect !== "function") {
    return Promise.resolve();
  }

  return new Promise(resolve => {
    let lastWidth = 0;
    let lastHeight = 0;
    let stableSince = 0;
    const startedAt = Date.now();

    const check = () => {
      const rect = iframe.getBoundingClientRect();
      const width = Math.round(rect.width);
      const height = Math.round(rect.height);
      const now = Date.now();

      if (width > 0 && height > 0 && width === lastWidth && height === lastHeight) {
        if (!stableSince) stableSince = now;
        if (now - stableSince >= stableMs) {
          resolve();
          return;
        }
      } else {
        lastWidth = width;
        lastHeight = height;
        stableSince = now;
      }

      if (now - startedAt >= timeoutMs) {
        resolve();
        return;
      }

      if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
        setTimeout(check, 16);
        return;
      }

      window.requestAnimationFrame(check);
    };

    check();
  });
}

async function waitForIframePresentationReady(iframe, options = {}) {
  if (!iframe) return;
  await waitForIframeVisibility(iframe, options);
  await waitForIframeStableSize(iframe, options);
  await waitForAnimationFrames(2);
}

export function clearIframePayloadSyncTimer(vm, timerField, tokenField = null) {
  if (vm && vm[timerField]) {
    clearTimeout(vm[timerField]);
    vm[timerField] = null;
  }

  if (vm && tokenField) {
    vm[tokenField] = (vm[tokenField] || 0) + 1;
  }
}

export function scheduleIframePayloadSync(vm, timerField, sendFn, options = {}) {
  const {
    tokenField = null,
    iframeResolver = null,
    readinessOptions = {},
  } = options;

  clearIframePayloadSyncTimer(vm, timerField, tokenField);

  const scheduledToken = vm && tokenField
    ? vm[tokenField]
    : null;

  const run = async () => {
    try {
      const iframe = typeof iframeResolver === "function" ? iframeResolver() : null;
      await waitForIframePresentationReady(iframe, readinessOptions);
      if (vm && tokenField && vm[tokenField] !== scheduledToken) return;
      sendFn();
    } finally {
      if (vm && (!tokenField || vm[tokenField] === scheduledToken)) {
        vm[timerField] = null;
      }
    }
  };

  if (!vm) {
    run();
    return;
  }

  vm[timerField] = setTimeout(run, 0);
}
